import { useTranslation } from 'react-i18next';
import { parseVideoUrl } from '@erp/domain/formations';
import type { ProFormationLesson } from '@erp/data-access/interfaces';

/**
 * Le cadre 16:9 de la leçon en cours.
 *
 * YouTube / Vimeo passent par une iframe (pas d'API JS chargée : on ne sait
 * donc pas quand la vidéo se termine, l'électricien coche lui-même). Un
 * fichier direct passe par <video>, et là `onEnded` permet de cocher tout
 * seul — c'est le seul cas où on peut le faire honnêtement.
 */
export function LessonPlayer({
  lesson,
  title,
  onEnded,
}: {
  lesson: ProFormationLesson;
  title: string;
  onEnded?: () => void;
}) {
  const { t } = useTranslation();
  const parsed = parseVideoUrl(lesson.videoUrl);

  return (
    // 16:9 strict, mais plafonné en hauteur : sur un portable, un 16:9 pleine
    // largeur poussait la fiche et le bouton « J'ai vu cette vidéo » sous la
    // ligne de flottaison. On borne la LARGEUR à ce que 62vh autorise.
    <div
      className="pro-player-frame relative mx-auto w-full"
      style={{ aspectRatio: '16 / 9', maxWidth: 'calc(62vh * 16 / 9)' }}
    >
      {parsed.provider === 'file' ? (
        <video
          key={lesson.id}
          src={parsed.embedUrl}
          controls
          playsInline
          preload="metadata"
          poster={lesson.thumbnailUrl ?? undefined}
          onEnded={onEnded}
          className="size-full"
        >
          {t('pro.formation.player_unsupported', 'Votre navigateur ne peut pas lire cette vidéo.')}
        </video>
      ) : (
        <iframe
          key={lesson.id}
          src={parsed.embedUrl}
          title={title}
          className="size-full border-0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
        />
      )}
    </div>
  );
}
